import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { ImageGallery } from '../../components/ImageGallery';
import { Modal } from '../../components/Modal';
import { PriceDisplay } from '../../components/PriceDisplay';
import { CloseIcon } from '../../icons/CloseIcon';
import { CloseIconSlot } from '../../icons/CloseIconSlot';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  AmenityIcon,
  AmenityList,
  CloseControl,
  CloseRow,
  Content,
  FooterContent,
  GalleryFrame,
  HeaderRow,
  MainBlock,
  MetaText,
  ModalTitle,
  NightPrice,
  PriceBlock,
  RichText,
  SectionBlock,
  SectionHeading,
  StayPrice,
  TextBlock,
  TitleBlock,
  TotalDueAmount,
  TotalDueRow,
} from './DetailModal.styles';
import type { DetailModalProps } from './DetailModal.types';

export function DetailModal({
  open,
  onClose,
  title,
  titleVariant = 'inline',
  ariaLabel,
  images,
  tags,
  pricePerStay,
  pricePerNight,
  showTotalDue = false,
  description,
  amenities,
  amenitiesVariant = 'bulleted',
  amenitiesHeading,
  terms,
  onReserve,
  reserveDisabled = false,
  locale,
  aboveMobileNav = false,
  labels = {},
}: DetailModalProps) {
  const {
    close = 'Close',
    perNight = 'night',
    totalDue = 'Total due',
    reserve = 'Reserve',
    termsHeading = 'Terms & Conditions',
    previousImage,
    nextImage,
    goToImage,
    fromPrice,
  } = labels;

  const galleryImages = images ?? [];
  const hasImages = galleryImages.length > 0;
  const metaText = tags?.filter(Boolean).join(' • ');
  const hasPrice = !!(pricePerStay || pricePerNight);

  const footer = onReserve ? (
    <FooterContent>
      <Button
        variant="primary"
        size="large"
        fullWidth
        disabled={reserveDisabled}
        onClick={onReserve}
      >
        {reserve}
      </Button>
    </FooterContent>
  ) : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel={ariaLabel ?? title}
      footer={footer}
      labels={{ close }}
      aboveMobileNav={aboveMobileNav}
      size="lg"
    >
      <Content>
        {hasImages ? (
          <GalleryFrame>
            <ImageGallery
              images={galleryImages}
              sticky={false}
              prevLabel={previousImage}
              nextLabel={nextImage}
              goToImageLabel={goToImage}
            />
            <CloseControl
              type="button"
              data-placement="floating"
              onClick={onClose}
              aria-label={close}
            >
              <CloseIconSlot>
                <CloseIcon size={10} />
              </CloseIconSlot>
            </CloseControl>
          </GalleryFrame>
        ) : (
          <CloseRow>
            <CloseControl type="button" onClick={onClose} aria-label={close}>
              <CloseIconSlot>
                <CloseIcon size={10} />
              </CloseIconSlot>
            </CloseControl>
          </CloseRow>
        )}
        <TextBlock data-has-footer={onReserve ? 'true' : 'false'}>
          <MainBlock>
            <HeaderRow>
              <TitleBlock>
                <ModalTitle data-title-variant={titleVariant}>{title}</ModalTitle>
                {metaText && <MetaText>{metaText}</MetaText>}
              </TitleBlock>
              {hasPrice && (
                <PriceBlock>
                  {pricePerStay &&
                    (fromPrice ? (
                      <PriceDisplay
                        amount={pricePerStay.amount}
                        currencyCode={pricePerStay.currencyCode}
                        label={fromPrice}
                        locale={locale}
                        size="base"
                      />
                    ) : (
                      <StayPrice>
                        {formatCurrency(pricePerStay.amount, pricePerStay.currencyCode, { locale })}
                      </StayPrice>
                    ))}
                  {pricePerNight && (
                    <NightPrice>
                      {formatCurrency(pricePerNight.amount, pricePerNight.currencyCode, { locale })}{' '}
                      /{perNight}
                    </NightPrice>
                  )}
                </PriceBlock>
              )}
            </HeaderRow>
            {description && <RichText data-variant="description">{description}</RichText>}
            {amenities && amenities.length > 0 && (
              <SectionBlock>
                {amenitiesHeading && (
                  <SectionHeading data-variant="list">{amenitiesHeading}</SectionHeading>
                )}
                <AmenityList data-variant={amenitiesVariant}>
                  {amenities.map((amenity) => (
                    <li key={amenity.id}>
                      {amenitiesVariant === 'detailed' && amenity.icon && (
                        <AmenityIcon>{amenity.icon}</AmenityIcon>
                      )}
                      {amenitiesVariant === 'badges' ? (
                        <Badge tone="neutral">{amenity.label}</Badge>
                      ) : (
                        <span>{amenity.label}</span>
                      )}
                    </li>
                  ))}
                </AmenityList>
              </SectionBlock>
            )}
          </MainBlock>
          {showTotalDue && pricePerStay && (
            <TotalDueRow>
              <span>{totalDue}</span>
              <TotalDueAmount>
                {formatCurrency(pricePerStay.amount, pricePerStay.currencyCode, { locale })}
              </TotalDueAmount>
            </TotalDueRow>
          )}
          {terms && (
            <SectionBlock>
              <SectionHeading>{termsHeading}</SectionHeading>
              <RichText data-variant="terms">{terms}</RichText>
            </SectionBlock>
          )}
        </TextBlock>
      </Content>
    </Modal>
  );
}
